<template>
  <div class="tw:flex tw:flex-col tw:gap-3">
    <table class="tw:w-full tw:text-sm">
      <thead>
        <tr class="tw:border-b tw:text-xs tw:text-muted-foreground">
          <th class="tw:w-px"></th>
          <th class="tw:py-2 tw:text-left tw:font-medium">{{ $t('Field') }}</th>
          <th
            v-for="place in contexts"
            :key="place.ctx"
            class="tw:hidden tw:md:table-cell tw:w-32 tw:px-2 tw:py-2 tw:font-medium"
          >
            <span class="tw:flex tw:items-center tw:justify-center tw:gap-1">
              <Icon :name="place.icon" />
              {{ $t(place.shortLabel) }}
            </span>
          </th>
          <th class="tw:w-px"></th>
        </tr>
      </thead>

      <Sortable
        :list="currentFieldsConfig"
        item-key="name"
        tag="tbody"
        class="tw:divide-y"
        :options="{handle:'.draggable-handle'}"
        @end="onSortEnd"
      >
        <template #item="{element: currentfieldConfig, index}">
          <Field
            :key="currentfieldConfig.name"
            :field="availableFieldFor(currentfieldConfig.name)"
            :field-config="currentfieldConfig"
            :form-fields="currentFieldsConfig"
            :condition-sources="conditionSources"
            :condition-effects="conditionEffects"
            @remove="removeAt(index)"
            @fieldUpdated="updateField(index, $event)"
          />
        </template>
      </Sortable>
    </table>

    <input type="hidden" :name="name" :value="stringifyFields">
    <AddField
      :available-fields="availableFields"
      :current-fields="currentFieldsConfig"
      v-on:add-fields="onAddFields"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Sortable } from "sortablejs-vue3";
import Field from './Field.vue'
import AddField from "./AddField.vue";
import type { AvailableField, ConditionChoice, FieldConfig } from "@/types/FieldConfig";
import contexts from './contexts';
import { Icon } from '@/components/ui/icon';

const props = defineProps<{
  name: string;
  fieldsConfig: FieldConfig[];
  availableFields: AvailableField[];
  conditionEffects?: ConditionChoice[];
}>();

const currentFieldsConfig = ref<FieldConfig[]>(props.fieldsConfig);

const stringifyFields = computed(() => JSON.stringify(currentFieldsConfig.value));

const availableFieldFor = (name: string): AvailableField | undefined =>
  props.availableFields.find(field => field.name === name);

const conditionEffects = computed<ConditionChoice[]>(() => props.conditionEffects ?? []);

/** Only fields already in the form can drive a rule, and only some of them can at all. */
const conditionSources = computed<AvailableField[]>(() =>
  currentFieldsConfig.value
    .filter(field => !field.condition)
    .map(field => availableFieldFor(field.name))
    .filter((field): field is AvailableField => Boolean(field?.condition_source)));

const onSortEnd = (event: { oldIndex: number; newIndex: number }) => {
  currentFieldsConfig.value.splice(event.newIndex, 0, currentFieldsConfig.value.splice(event.oldIndex, 1)[0]);
}

const removeAt = (idx: number) => {
  currentFieldsConfig.value.splice(idx, 1);
}

const updateField = (i: number, field: FieldConfig) => {
  currentFieldsConfig.value.splice(i, 1, field);
}

/**
 * A field can only appear once: the list is keyed by name, and a duplicate would
 * render twice and be stored twice.
 */
const onAddFields = (fields: FieldConfig[]) => {
  const present = new Set(currentFieldsConfig.value.map(field => field.name));

  currentFieldsConfig.value.push(...fields.filter(field => !present.has(field.name)));
}
</script>
